import { useTranslation } from 'react-i18next'
import { CalendarDays, ChevronRight, Coins, Eye, EyeOff, Globe, ListOrdered, Pencil, User, UserX, Users, Wallet } from 'lucide-react'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { InfoCell } from '@/components/common/InfoCell'
import { ShopModuleTypeIcon, shopModuleLabel } from '@/pages/groups/components/ShopModuleIcon'
import { usePermissions } from '@/hooks/usePermissions'
import { PermissionEntityType } from '@/api/model'
import type { BaseShopModuleModel } from '@/api/model'
import type { WalletTypeDisplay } from '@/lib/walletTypes'

interface Props {
  module: BaseShopModuleModel
  /** Resolved by the page — the model only carries the wallet type id. */
  walletType: WalletTypeDisplay
  /** Resolved by the page — the model only carries group ids. Tooltip content only. */
  groupNames: string[]
  /** Set only for types that have a detail page — otherwise the card is not clickable. */
  onOpen?: (module: BaseShopModuleModel) => void
  onEdit: (module: BaseShopModuleModel) => void
}

/**
 * Panel of a shop module. Shop modules have no name, description or icon of their own
 * (see `claude-docs/features/shop-modules.md`), so the type label is the title and the icon
 * comes from the fixed type map shared with the groups pages. Everything but the wallet
 * type is display only — that one property is editable via the pencil.
 */
export function ShopModuleCard({ module, walletType, groupNames, onOpen, onEdit }: Props) {
  // `groups` is declared because shopModuleLabel() reads its type labels from that NS.
  const { t } = useTranslation(['shop-modules', 'groups'])
  const { userHasEntityPermission } = usePermissions()
  const canEdit = userHasEntityPermission(PermissionEntityType.ShopModules, 'Edit', module.id)

  const groupCount = module.assignedGroups?.length ?? 0
  const employeeCount = module.assignedEmployeeUsers?.length ?? 0
  // A module that is neither company-wide nor assigned to anyone is a real state here —
  // it explains why employees don't see the module at all.
  const hasNoAssignment = !module.isCompanyWideAssigned && groupCount === 0 && employeeCount === 0

  const walletTypeValue =
    walletType.kind === 'named' ? walletType.name
      : walletType.kind === 'fallback' ? t('card.walletFallback')
        : '—'

  // Only types with a detail page become interactive; the rest stay plain panels.
  const clickProps = onOpen
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: () => onOpen(module),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onOpen(module)
          }
        },
      }
    : {}

  return (
    <div
      {...clickProps}
      className={`flex h-full flex-col border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-card${
        onOpen ? ' cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' : ''
      }`}
    >
      {/* Header: type icon + type label (no name field exists) */}
      <div className="h-14 flex items-center gap-3 px-4 border-b">
        <div className="flex size-8 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-muted/40">
          <ShopModuleTypeIcon type={module.type} className="size-5" />
        </div>
        <p className="font-semibold text-sm flex-1 truncate">{shopModuleLabel(module.type)}</p>
        {canEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            // Stop propagation: on types with a detail page the whole card is clickable.
            onClick={(e) => {
              e.stopPropagation()
              onEdit(module)
            }}
          >
            <Pencil className="size-3.5" />
          </Button>
        )}
        {onOpen && <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
      </div>

      {/* Info grid — same two-column layout as ActionCard */}
      <div className="grid grid-cols-[1fr_minmax(0,max-content)] gap-x-4 gap-y-2 p-3 text-xs">
        <InfoCell
          icon={Coins}
          label={t('card.budget')}
          value={module.maxAmount != null ? t('card.points', { count: module.maxAmount }) : t('card.unlimited')}
        />
        <InfoCell
          icon={ListOrdered}
          label={t('card.priority')}
          value={module.priority != null ? String(module.priority) : '—'}
        />
        <InfoCell
          icon={CalendarDays}
          label={t('card.orderDay')}
          value={t('card.orderDayValue', { day: module.orderDayInMonth })}
        />
        <InfoCell
          icon={Wallet}
          label={t('card.walletType')}
          value={walletTypeValue}
          tooltip={walletType.kind === 'named' ? walletType.name : undefined}
        />
      </div>

      {/* Status badges (pattern from ServiceModuleCard) */}
      <div className="mt-auto flex flex-wrap gap-1 border-t p-3">
        <Badge variant={module.isVisible ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0 gap-1">
          {module.isVisible ? <Eye className="size-2.5" /> : <EyeOff className="size-2.5" />}
          {module.isVisible ? t('card.visible') : t('card.hidden')}
        </Badge>

        {module.isCompanyWideAssigned && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
            <Globe className="size-2.5" />
            {t('card.all')}
          </Badge>
        )}
        {groupCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1 cursor-default">
                <Users className="size-2.5" />
                {t('card.groups', { count: groupCount })}
              </Badge>
            </TooltipTrigger>
            {groupNames.length > 0 && (
              <TooltipContent>
                <div className="flex flex-col gap-0.5">
                  {groupNames.map((name) => <span key={name}>{name}</span>)}
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        )}
        {employeeCount > 0 && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
            <User className="size-2.5" />
            {t('card.employees', { count: employeeCount })}
          </Badge>
        )}
        {hasNoAssignment && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 gap-1">
            <UserX className="size-2.5" />
            {t('card.noAssignment')}
          </Badge>
        )}
      </div>
    </div>
  )
}
